import React, { type ReactNode, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { AuthContext } from "@/hooks/useAuthContext";
import { setSentryUser } from "@/lib/sentry";
import { identifyMixpanelUser, trackWebAppOpened } from "@/lib/mixpanel";

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const auth = useAuth();

  // Track user state changes in Sentry
  useEffect(() => {
    if (auth.user) {
      const { uid, email, displayName } = auth.user;

      setSentryUser(auth.user, {
        accountId: auth.getAccountId() || null,
        isSuperAdmin: auth.userProfile?.isSuperAdmin || false,
        isAccountAdmin: auth.isAccountAdmin(),
      });

      identifyMixpanelUser(uid, email, auth.getAccountId());
      trackWebAppOpened(auth.getAccountId());

      if (import.meta.env.DEV) {
        console.log("User set:", {
          uid,
          email,
          displayName,
          officeId: auth.getAccountId(),
          isSuperAdmin: auth.userProfile?.isSuperAdmin,
          officeRoles: auth.userProfile?.officeRoles,
        });
      }
    } else {
      setSentryUser(null);
    }
  }, [auth.user, auth.userProfile]);

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};
